"use client";

import { Text, Tooltip } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import { ButtonWrapper } from "../../common/ButtonWrapper";
import { isNewlyCreated, type FolderTreeNode } from "./folderTree";
import styles from "./FolderPickerModal.module.css";

interface FolderRowProps {
  node: FolderTreeNode;
  isSelected: boolean;
  isDisabled?: boolean;
  /**
   * Tooltip copy shown when the row is disabled (e.g. blocked by the
   * subfolder-depth limit). When present and the row is disabled, the row is
   * wrapped in a tooltip explaining why it can't be selected.
   */
  disabledTooltip?: string;
  hasChildren: boolean;
  isExpanded: boolean;
  onSelect: (id: string) => void;
  onToggleExpanded: (id: string) => void;
  onDelete: (id: string) => void;
  isMutating: boolean;
}

export function FolderRow({
  node,
  isSelected,
  isDisabled = false,
  disabledTooltip,
  hasChildren,
  isExpanded,
  onSelect,
  onToggleExpanded,
  onDelete,
  isMutating,
}: FolderRowProps) {
  const indent = node.depth * 24;
  const iconClass = isSelected ? styles.folderIconSelected : styles.folderIcon;
  // Newly-created folders are personal (we never create shared folders from
  // the modal); so they always use the `navFolder` icon.
  const isShared =
    !isNewlyCreated(node) && (node as { isShared?: boolean }).isShared === true;

  const rowButton = (
    <button
      type="button"
      className={`${styles.folderRow} ${
        isSelected ? styles.folderRowSelected : ""
      } ${isDisabled ? styles.folderRowDisabled : ""}`.trim()}
      onClick={() => {
        if (isDisabled) return;
        onSelect(node.id);
      }}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      style={{ paddingInlineStart: indent + 8 }}
    >
      {/* Chevron is an independent click target so clicking it toggles the
          subtree without also selecting this folder as the destination.
          Leaf folders show no chevron at all (matches the main folders
          page's `FolderTreeItem`); we render an empty spacer in its place
          so the folder icon stays aligned with parent rows. */}
      {hasChildren ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- chevron is a secondary mouse-only toggle nested inside the row button; it cannot be a nested button and the row's keyboard activation selects the folder
        <span
          className={iconClass}
          onClick={(event) => {
            event.stopPropagation();
            if (isDisabled) return;
            onToggleExpanded(node.id);
          }}
        >
          <Icon
            name={isExpanded ? "arrowSmallDown" : "arrowSmallRight"}
            size="1.5em"
          />
        </span>
      ) : (
        <span className={styles.chevronSpacer} aria-hidden />
      )}
      <span className={iconClass} aria-hidden>
        <Icon name={isShared ? "team" : "navFolder"} size="1.5em" />
      </span>
      <Text
        type="Body2"
        tag="span"
        className={styles.folderName}
        color={isSelected ? "TextLink" : isDisabled ? "TextWeaker" : undefined}
      >
        {node.name}
      </Text>
      {isSelected ? (
        <span aria-label="selected" className={styles.folderCheck}>
          <Icon name="formCheck" size="1.25em" />
        </span>
      ) : undefined}
      {isNewlyCreated(node) ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- delete affordance is a secondary mouse-only target nested inside the row button; it cannot be a nested button
        <span
          className={styles.trashButton}
          onClick={(event) => {
            event.stopPropagation();
            if (!isMutating) onDelete(node.id);
          }}
        >
          <Icon name="actionTrash" size="1em" />
        </span>
      ) : undefined}
    </button>
  );

  // A disabled row can't be a focusable trigger, so wrap it in
  // `ButtonWrapper` (a non-focusable tooltip trigger) to surface the reason
  // it can't be selected on hover/focus.
  if (isDisabled && disabledTooltip) {
    return (
      <Tooltip allowMultiline content={disabledTooltip}>
        <ButtonWrapper isDisabled>{rowButton}</ButtonWrapper>
      </Tooltip>
    );
  }

  return rowButton;
}
